import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import {
  fetchPublicPartnership,
  PublicPartnership,
  PartnershipPack,
  PartnershipPreview,
  PartnershipAddon,
  PartnershipContact,
  submitPublicOptIn,
} from '../lib/api/partnerships';
import { parseDate } from '../lib/dates';
import './partnership-font.css';
import { PlacementMap } from '../components/placements/PlacementMap';
import BookingSection, { priceOf } from '../components/partnerships/BookingSection';
import { LANGS, Lang, langForCountry, makeT, fmtDayMonth, fmtLongDate as fmtLongL, fmtRange as fmtRangeL } from '../lib/bookingI18n';
import { RATE_BY_PARTNERSHIP_ITEM, pagesWithAny } from '../lib/placementMap';

/* ============================================================
   LampTwist editorial design tokens (mirror of the Claude Design
   "Mosy - Partnership Page" bundle). TWK Lausanne 300 is loaded via
   partnership-font.css; Inter is the fallback while it swaps in.
   ============================================================ */
const LT = {
  paper: '#fbfbfa',
  bone: '#edece5',
  boneDark: '#dedccf',
  ink: '#2b4138',
  ink700: '#3d5448',
  ink500: '#6d8276',
  ink300: '#a8b6ad',
  yellow: '#ffbd09',
  yellow200: '#ffe79a',
  yellow50: '#fff6d9',
  cta: '#469e7a',
  ctaHover: '#3a8767',
  border: 'rgba(43,65,56,0.12)',
  borderStrong: 'rgba(43,65,56,0.2)',
  rCard: 14,
  rPill: 999,
};
const FONT =
  '"TWK Lausanne", "Inter", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const fmtEUR = (n: number) =>
  n === 0 ? 'Free' : `€${n.toLocaleString('en', { maximumFractionDigits: 0 })}`;

const rangeLabel = (a: string, b: string): string => {
  const sa = parseDate(a);
  const sb = parseDate(b);
  const same = sa.getMonth() === sb.getMonth() && sa.getFullYear() === sb.getFullYear();
  if (same) return `${MONTHS_LONG[sa.getMonth()]} ${sa.getDate()}–${sb.getDate()}, ${sa.getFullYear()}`;
  return `${MONTHS[sa.getMonth()]} ${sa.getDate()} – ${MONTHS[sb.getMonth()]} ${sb.getDate()}, ${sb.getFullYear()}`;
};
const fmtLong = (s: string): string => {
  const d = parseDate(s);
  return `${MONTHS_LONG[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};
const splitParagraphs = (t: string): string[] =>
  (t || '').split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
const splitLines = (t: string): string[] =>
  (t || '').split(/\n+/).map((l) => l.trim()).filter(Boolean);

/* ---------- icons (Lucide-ish, 1.5 stroke) ---------- */
const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5 10 17l9-10" /></svg>
);
const IconMinus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M5 12h14" /></svg>
);
const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
);
const IconMail = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" /><path d="M3 7l9 6 9-6" /></svg>
);
const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);
const IconCopy = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="11" height="11" /><path d="M5 15V5a1 1 0 0 1 1-1h10" /></svg>
);
const IconCalendar = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" /><path d="M3 9h18M8 3v4M16 3v4" /></svg>
);

const Halo = ({ size = 22 }: { size?: number }) => (
  <span
    style={{
      width: size, height: size, borderRadius: '50%', background: LT.yellow,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}
  >
    <img
      src="/lamptwist-mark.svg"
      alt=""
      style={{ width: size * 0.6, height: size * 0.6, display: 'block' }}
    />
  </span>
);

const Eyebrow = ({ children, color }: { children: React.ReactNode; color?: string }) => (
  <div style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: color || LT.ink500, lineHeight: 1.2 }}>
    {children}
  </div>
);

const WRAP: React.CSSProperties = { maxWidth: 1520, margin: '0 auto', padding: '0 32px' };

/* ---------- italic-last-word display heading ---------- */
const SplitHeading = ({
  text, size, max, className,
}: { text: string; size: number | string; max?: string; className?: string }) => {
  const words = text.trim().split(' ');
  const head = words.slice(0, -1).join(' ');
  const tail = words.slice(-1)[0];
  return (
    <h2 className={className} style={{ fontSize: size, lineHeight: 1.03, letterSpacing: '-0.025em', fontWeight: 300, margin: 0, maxWidth: max, color: 'inherit' }}>
      {head}{head ? ' ' : ''}
      <span style={{ fontStyle: 'italic', color: LT.ink700 }}>{tail}.</span>
    </h2>
  );
};

/* ============================================================
   Responsive overrides.

   Inline styles in JSX are the DESKTOP defaults. The CSS below
   uses `!important` only where it needs to beat an inline style
   on tablet (≤1023) and mobile (≤639). Class names are namespaced
   `lt-pp-*` so they don't collide with anything else in mosy.
   ============================================================ */
const RESPONSIVE_CSS = `
/* Rich-text in the "Good to know" section. Lists get the inset-border
   look; paragraphs and headings stay airy. */
.lt-pp-extras-rt > *:first-child { margin-top: 0; }
.lt-pp-extras-rt > *:last-child { margin-bottom: 0; }
.lt-pp-extras-rt p { margin: 0 0 14px; }
.lt-pp-extras-rt h2 { font-size: 22px; font-weight: 500; margin: 24px 0 10px; letter-spacing: -0.01em; }
.lt-pp-extras-rt h3 { font-size: 18px; font-weight: 600; margin: 20px 0 8px; }
.lt-pp-extras-rt ul, .lt-pp-extras-rt ol { padding-left: 22px; margin: 0 0 14px; }
.lt-pp-extras-rt li { padding: 4px 0; }
.lt-pp-extras-rt a { color: inherit; text-decoration: underline; text-underline-offset: 3px; }
.lt-pp-extras-rt strong { font-weight: 600; }
.lt-pp-extras-rt em { font-style: italic; }
.lt-pp-extras-rt code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.92em; background: rgba(0,0,0,0.04); padding: 1px 6px; border-radius: 4px; }
/* Brand-note textarea — sits on the dark ink card; default placeholder
   contrast is too low, so bump it to a readable off-white. */
.lt-pp-brand-note::placeholder { color: rgba(251,251,250,0.55); opacity: 1; }
.lt-pp-brand-note::-webkit-input-placeholder { color: rgba(251,251,250,0.55); }
.lt-pp-brand-note::-moz-placeholder { color: rgba(251,251,250,0.55); opacity: 1; }
@media (max-width: 639px) {
  .lt-pp-welcome-title { font-size: 18px !important; }
}
/* Tablet & below — collapse multi-column layouts, dial padding */
@media (max-width: 1023px) {
  .lt-pp-strip { padding: 14px 24px !important; }
  .lt-pp-strip-eyebrow { display: none !important; }

  .lt-pp-section { padding: 80px 24px !important; }
  .lt-pp-hero { padding: 64px 24px 80px !important; }
  .lt-pp-stats-section .lt-pp-stats-wrap { padding: 56px 24px !important; }

  .lt-pp-hero-lead { font-size: 18px !important; margin-top: 24px !important; }
  .lt-pp-hero-more { font-size: 16px !important; }
  .lt-pp-hero-brands { font-size: 14px !important; }
  .lt-pp-hero-cta { margin-top: 40px !important; padding-top: 28px !important; gap: 22px !important; }

  .lt-pp-section-head { margin-bottom: 36px !important; }
  .lt-pp-section-title { font-size: 44px !important; }

  .lt-pp-packs-grid { grid-template-columns: 1fr !important; gap: 16px !important; }
  .lt-pp-pack-card { min-height: 0 !important; padding: 28px 24px 24px !important; }
  .lt-pp-pack-name { font-size: 36px !important; }
  .lt-pp-pack-price { font-size: 48px !important; }

  .lt-pp-addons-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 16px !important; }
  .lt-pp-previews-cols { column-count: 2 !important; column-gap: 16px !important; }

  .lt-pp-stats-grid { grid-template-columns: repeat(3, 1fr) !important; gap: 28px 24px !important; }
  .lt-pp-stats-cell { border-left: none !important; padding-left: 0 !important; padding-right: 16px !important; }
  .lt-pp-stats-value { font-size: 48px !important; }
  .lt-pp-stats-title { font-size: 34px !important; }

  .lt-pp-two-col,
  .lt-pp-two-col-equal { grid-template-columns: 1fr !important; gap: 24px !important; }
  .lt-pp-optin-grid { grid-template-columns: 1fr !important; gap: 20px !important; }
  .lt-pp-card { padding: 36px 28px !important; }
  .lt-pp-card-title { font-size: 36px !important; }

  .lt-pp-page-pad { padding-bottom: 156px !important; }
}

/* Mobile */
@media (max-width: 639px) {
  .lt-pp-strip { padding: 12px 16px !important; gap: 12px !important; }
  .lt-pp-strip img { height: 18px !important; }

  .lt-pp-section { padding: 64px 18px !important; }
  .lt-pp-hero { padding: 56px 18px 64px !important; }
  .lt-pp-stats-section .lt-pp-stats-wrap { padding: 44px 18px !important; }

  .lt-pp-hero-lead { font-size: 17px !important; margin-top: 22px !important; }
  .lt-pp-hero-more { font-size: 15.5px !important; }
  .lt-pp-hero-cta { gap: 18px !important; }
  .lt-pp-hero-cta a { width: 100% !important; justify-content: center !important; padding: 14px 22px !important; }

  .lt-pp-section-head { gap: 18px !important; }
  .lt-pp-section-title { font-size: 36px !important; }

  .lt-pp-addons-grid { grid-template-columns: 1fr !important; }
  .lt-pp-previews-cols { column-count: 1 !important; }

  .lt-pp-stats-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 24px 18px !important; }
  .lt-pp-stats-value { font-size: 40px !important; }
  .lt-pp-stats-title { font-size: 28px !important; }

  .lt-pp-card { padding: 32px 22px !important; }
  .lt-pp-card-title { font-size: 30px !important; }
  .lt-pp-extras-rt { font-size: 15px !important; }

  /* Sticky calculator — stacked, but dialled down so the bar takes only the height
     its content actually needs. No filler whitespace. */
  .lt-pp-sticky-bar {
    padding: 12px 16px !important;
    gap: 10px !important;
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .lt-pp-sticky-info { flex: 0 0 auto !important; min-width: 0 !important; }
  .lt-pp-sticky-info > div:first-child { font-size: 10px !important; margin-bottom: 2px !important; }
  .lt-pp-sticky-info > div:nth-child(2) { gap: 10px !important; }
  .lt-pp-sticky-info .lt-pp-sticky-price + div { font-size: 11px !important; }
  .lt-pp-sticky-info > div:last-child { margin-top: 4px !important; }
  .lt-pp-sticky-price { font-size: 26px !important; }
  .lt-pp-sticky-copy { display: none !important; }
  .lt-pp-sticky-cta-wrap { width: 100% !important; gap: 8px !important; }
  .lt-pp-sticky-cta-wrap > a {
    width: 100% !important;
    justify-content: center !important;
    padding: 11px 18px !important;
    font-size: 13px !important;
  }

  .lt-pp-page-pad { padding-bottom: 152px !important; }
}
`;

/* ============================================================ */

const PackCard = ({
  pack, features, selected, onSelect,
}: {
  pack: PartnershipPack;
  features: PublicPartnership['features'];
  selected: boolean;
  onSelect: () => void;
}) => {
  const [hover, setHover] = useState(false);
  const featureSet = useMemo(() => new Set(pack.includes), [pack.includes]);
  const hi = !!pack.highlight;
  const soldOut = !!pack.sold_out;
  return (
    <button
      type="button"
      onClick={soldOut ? undefined : onSelect}
      disabled={soldOut}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="lt-pp-pack-card"
      style={{
        textAlign: 'left', fontFamily: FONT, fontWeight: 300,
        background: LT.paper,
        color: LT.ink,
        border: selected ? `1px solid ${LT.ink}` : hi ? `1px solid ${LT.ink}` : `1px solid ${LT.border}`,
        outline: selected ? `3px solid ${LT.yellow}` : 'none',
        outlineOffset: selected ? -1 : 0,
        padding: '32px 28px 28px',
        borderRadius: LT.rCard,
        cursor: soldOut ? 'not-allowed' : 'pointer',
        transition: 'all 200ms cubic-bezier(.2,0,0,1)',
        display: 'flex', flexDirection: 'column', gap: 22, position: 'relative',
        boxShadow: hover && !selected && !soldOut ? '0 6px 18px -8px rgba(43,65,56,0.18)' : 'none',
        minHeight: 540,
        opacity: soldOut ? 0.55 : 1,
        filter: soldOut ? 'grayscale(0.4)' : 'none',
      }}
    >
      {hi && !soldOut && (
        <div style={{ position: 'absolute', top: -22, right: 24 }}>
          <Halo size={44} />
        </div>
      )}
      {soldOut && (
        <div style={{
          position: 'absolute', top: 16, right: 16,
          fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase',
          color: '#7a1e1e', background: '#fbe7e7',
          border: '1px solid #f0c4c4', borderRadius: LT.rPill,
          padding: '4px 12px', fontWeight: 600,
        }}>
          No longer available
        </div>
      )}
      <div>
        {hi && (
          <div style={{ display: 'inline-block', fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: LT.ink, background: LT.yellow50, border: `1px solid ${LT.yellow}`, borderRadius: LT.rPill, padding: '3px 12px', marginBottom: 14 }}>
            Best value
          </div>
        )}
        <h3 className="lt-pp-pack-name" style={{ margin: 0, fontSize: 42, letterSpacing: '-0.02em', lineHeight: 1.02, fontWeight: 300 }}>{pack.name}</h3>
      </div>
      <div>
        <div className="lt-pp-pack-price" style={{ fontSize: 56, letterSpacing: '-0.025em', lineHeight: 1, fontWeight: 300 }}>{fmtEUR(pack.price)}</div>
        <div style={{ fontSize: 12, color: LT.ink500, marginTop: 6 }}>One-time · for the full campaign</div>
        {pack.scarcity_note && (
          <div style={{ display: 'inline-block', marginTop: 12, fontSize: 11, letterSpacing: '0.06em', padding: '4px 10px', borderRadius: LT.rPill, background: LT.yellow50, color: '#876200' }}>
            {pack.scarcity_note}
          </div>
        )}
      </div>
      {pack.blurb && (
        <p style={{ fontSize: 14, lineHeight: 1.55, margin: 0, color: LT.ink700, maxWidth: '34ch' }}>{pack.blurb}</p>
      )}
      <div style={{ height: 1, background: LT.border }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {features.map((f) => {
          const inc = featureSet.has(f.id);
          const col = inc ? LT.ink : LT.ink300;
          return (
            <div key={f.id}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, lineHeight: 1.45, color: col }}>
                <span style={{ width: 14, height: 14, flexShrink: 0, marginTop: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: inc ? LT.cta : col }}>
                  {inc ? <IconCheck /> : <IconMinus />}
                </span>
                <span style={{ textDecoration: inc ? 'none' : 'line-through' }}>{f.label}</span>
              </div>
              {inc && f.children && f.children.length > 0 && (
                <div style={{ marginLeft: 24, marginTop: 5, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {f.children.map((c) => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: LT.ink500 }}>
                      <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'currentColor', flexShrink: 0 }} />
                      {c.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div
        style={{
          marginTop: 'auto', padding: '14px 22px', borderRadius: LT.rPill,
          background: selected ? LT.yellow : LT.bone,
          color: LT.ink,
          fontSize: 13, letterSpacing: '0.04em', textAlign: 'center',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        {selected ? <><IconCheck /> Selected</> : <>Select {pack.name}</>}
      </div>
    </button>
  );
};

const AddonCard = ({
  addon, selected, eligible, eligibilityLabel, onToggle,
}: {
  addon: PartnershipAddon;
  selected: boolean;
  eligible: boolean;
  eligibilityLabel: string | null;
  onToggle: () => void;
}) => {
  const [hover, setHover] = useState(false);
  const priceTag = addon.price > 0 ? fmtEUR(addon.price) : 'Free';
  const dividerColor = LT.border;

  const metaLines: { text: string; warn?: boolean }[] = [];
  if (addon.price === 0 && addon.price_note) {
    metaLines.push({ text: addon.price_note });
  }
  if (eligibilityLabel) {
    metaLines.push({
      text:
        eligible || eligibilityLabel.startsWith('Select a pack')
          ? eligibilityLabel
          : `${eligibilityLabel} — pick an eligible pack`,
      warn: !eligible,
    });
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={!eligible}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        textAlign: 'left', fontFamily: FONT, fontWeight: 300,
        background: LT.paper,
        color: LT.ink,
        border: selected || addon.recommended ? `1px solid ${LT.ink}` : `1px solid ${LT.border}`,
        borderRadius: LT.rCard, padding: '26px 26px',
        cursor: eligible ? 'pointer' : 'not-allowed',
        opacity: eligible ? 1 : 0.5,
        transition: 'all 200ms cubic-bezier(.2,0,0,1)',
        display: 'flex', flexDirection: 'column', gap: 14, position: 'relative',
        boxShadow: hover && eligible && !selected ? '0 6px 18px -8px rgba(43,65,56,0.12)' : 'none',
        outline: selected ? `3px solid ${LT.yellow}` : 'none', outlineOffset: -1,
      }}
    >
      {addon.recommended && (
        <div style={{ display: 'inline-block', alignSelf: 'flex-start', fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: LT.ink, background: LT.yellow50, border: `1px solid ${LT.yellow}`, borderRadius: LT.rPill, padding: '3px 12px' }}>
          Recommended
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16 }}>
        <div style={{ fontSize: 19, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
          {addon.label}
          {addon.campaign_specific && (
            <span style={{ marginLeft: 8, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: LT.ink500, border: `1px solid ${LT.border}`, borderRadius: LT.rPill, padding: '1px 8px' }}>
              Campaign extra
            </span>
          )}
        </div>
        <div style={{ fontSize: 26, letterSpacing: '-0.02em', lineHeight: 1, color: LT.ink, whiteSpace: 'nowrap' }}>{priceTag}</div>
      </div>
      {addon.description && (
        <p style={{ fontSize: 14, lineHeight: 1.55, margin: 0, color: LT.ink700 }}>{addon.description}</p>
      )}
      {addon.days ? (
        <div style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: '0.01em', color: LT.ink700 }}>
          ≈ {addon.days} day{addon.days === 1 ? '' : 's'} within the campaign
        </div>
      ) : null}
      {(metaLines.length > 0) && (
        <div
          style={{
            borderTop: `1px solid ${dividerColor}`,
            paddingTop: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
          }}
        >
          {metaLines.map((line, i) => (
            <div
              key={i}
              style={{
                fontSize: 11.5,
                letterSpacing: '0.02em',
                color: line.warn ? '#876200' : LT.ink500,
                fontWeight: line.warn ? 400 : 300,
              }}
            >
              {line.text}
            </div>
          ))}
        </div>
      )}
      <div
        style={{
          borderTop: `1px solid ${dividerColor}`,
          paddingTop: 16,
          marginTop: 'auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
        }}
      >
        {addon.scarcity_note ? (
          <span style={{ fontSize: 11, letterSpacing: '0.06em', padding: '3px 10px', borderRadius: LT.rPill, background: LT.yellow50, color: '#876200' }}>
            {addon.scarcity_note}
          </span>
        ) : <span />}
        {eligible && (
          <span
            style={{
              fontSize: 12, letterSpacing: '0.04em',
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '8px 16px', borderRadius: LT.rPill,
              border: `1px solid ${selected ? LT.yellow : LT.borderStrong}`,
              background: selected ? LT.yellow : 'transparent',
              color: LT.ink,
            }}
          >
            {selected ? <><IconCheck /> Added</> : <><IconPlus /> Add to pack</>}
          </span>
        )}
      </div>
    </button>
  );
};

const PreviewCard = ({ preview, onOpen }: { preview: PartnershipPreview; onOpen: (src: string) => void }) => (
  <figure
    style={{
      margin: '0 0 18px', background: LT.paper, border: `1px solid ${LT.border}`,
      borderRadius: LT.rCard, overflow: 'hidden', breakInside: 'avoid',
      display: 'block', width: '100%',
    }}
  >
    {preview.image ? (
      <button
        type="button"
        onClick={() => onOpen(preview.image as string)}
        title="Click to view full size"
        style={{ display: 'block', width: '100%', padding: 0, border: 0, background: LT.bone, cursor: 'zoom-in' }}
      >
        {/* Full image, natural proportions — never cropped. */}
        <img src={preview.image} alt={preview.label} style={{ width: '100%', height: 'auto', display: 'block' }} />
      </button>
    ) : (
      <div style={{ aspectRatio: '16 / 10', background: LT.bone, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: 24, color: LT.ink500 }}>
          <Halo size={40} />
          <div style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: 14 }}>{preview.label}</div>
          <div style={{ fontSize: 12, color: LT.ink300, marginTop: 6 }}>Visual coming soon</div>
        </div>
      </div>
    )}
    <figcaption style={{ padding: '18px 22px 20px' }}>
      <div style={{ fontSize: 16, color: LT.ink }}>{preview.label}</div>
      {preview.caption && (
        <div style={{ fontSize: 13, color: LT.ink500, marginTop: 6, lineHeight: 1.55 }}>{preview.caption}</div>
      )}
    </figcaption>
  </figure>
);

/* ============================================================ */

const CampaignPartnership = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  // Booking pages: EN / NL / FR. ?lang= wins, then the visitor's last pick,
  // then the brand's country (set once the page has loaded).
  const langKey = `pp-lang:${slug}`;
  const [lang, setLangState] = useState<Lang | null>(() => {
    const q = searchParams.get('lang');
    if (q === 'en' || q === 'nl' || q === 'fr') return q;
    try {
      const v = localStorage.getItem(`pp-lang:${slug}`);
      if (v === 'en' || v === 'nl' || v === 'fr') return v;
    } catch { /* private mode */ }
    return null;
  });
  const setLang = (l: Lang) => {
    setLangState(l);
    try { localStorage.setItem(langKey, l); } catch { /* private mode */ }
  };
  const token = searchParams.get('token') || '';
  const optInMode = !!token;
  const [data, setData] = useState<PublicPartnership | null>(null);
  const [status, setStatus] = useState<'loading' | 'notfound' | 'ready' | 'error'>('loading');
  const [selectedPack, setSelectedPack] = useState<string | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<Set<string>>(new Set());
  // Discount starts at 0 — brand has to actively engage with the slider.
  // Campaign minimum is 15%; the Confirm button is gated on that. If
  // they're returning to edit, the existing value hydrates from the row.
  const [discount, setDiscount] = useState<number>(0);
  const [brandNote, setBrandNote] = useState<string>('');
  // Sign-up is final on pages without packs: brands tick "I agree" first.
  const [agreed, setAgreed] = useState(false);
  // Booking pages: the slot placements the brand ticked.
  const [selectedSlots, setSelectedSlots] = useState<Set<number>>(new Set());
  const toggleSlot = (id: number) =>
    setSelectedSlots((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  // The selection overview card: when it's off screen and something is
  // ticked, a slim bar at the bottom leads to it (Doreen, 2026-10-23).
  const [overviewEl, setOverviewEl] = useState<HTMLDivElement | null>(null);
  const [overviewInView, setOverviewInView] = useState(false);
  useEffect(() => {
    if (!overviewEl || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) => setOverviewInView(e.isIntersecting), { threshold: 0.15 });
    io.observe(overviewEl);
    return () => io.disconnect();
  }, [overviewEl]);
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  // This partner-facing page wears the LampTwist mark (black), not the
  // mosy favicon. Restore on unmount so the admin app keeps its icon.
  useEffect(() => {
    const link = document.querySelector("link[rel='icon']");
    if (!link) return;
    const prev = link.getAttribute('href');
    link.setAttribute('href', '/lamptwist-mark.svg');
    return () => {
      if (prev) link.setAttribute('href', prev);
    };
  }, []);

  // Browser-tab title: the campaign, not "mosy". Restore on unmount.
  useEffect(() => {
    const prev = document.title;
    if (data) document.title = `${data.campaign.name} · LampTwist partnership`;
    return () => {
      document.title = prev;
    };
  }, [data]);

  useEffect(() => {
    let alive = true;
    setStatus('loading');
    fetchPublicPartnership(slug, token || undefined)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setStatus('ready');
        // Hydrate the selection from the brand's existing row, if any.
        const pre = d.response_preselection;
        if (pre) {
          if (pre.selected_pack) setSelectedPack(pre.selected_pack);
          if (pre.selected_addons?.length) {
            setSelectedAddons(new Set(pre.selected_addons));
          }
          if (pre.discount) {
            const n = parseInt(pre.discount.replace(/[^\d]/g, ''), 10);
            if (Number.isFinite(n) && n > 0) setDiscount(Math.min(30, n));
          }
          if (pre.brand_note) setBrandNote(pre.brand_note);
        }
        // Booking pages: the selection the team prepared for this brand.
        if (d.booking && !d.booking.signed_up && d.booking.proposed_slots?.length) {
          setSelectedSlots(new Set(d.booking.proposed_slots));
        }
      })
      .catch((err) => {
        if (!alive) return;
        const httpStatus = (err as { response?: { status?: number } })?.response?.status;
        setStatus(httpStatus === 404 ? 'notfound' : 'error');
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  // brand_responsibilities is HTML (TipTap-edited). For legacy plain-text
  // values (no tags) we still render gracefully by wrapping lines.
  // Must be computed BEFORE the early returns below — hook order must be
  // stable across renders.
  const effLang: Lang = !data || data.uses_packs ? 'en' : (lang ?? langForCountry(data.response_brand?.country));
  const tr = effLang !== 'en' ? data?.translations?.[effLang] : undefined;
  const pick = (en: string | undefined, x: string | undefined) => (x && x.trim() ? x : en || '');
  const responsibilitiesHtml = useMemo(() => {
    const raw = pick(data?.brand_responsibilities, tr?.brand_responsibilities);
    if (!raw.trim()) return '';
    const isHtml = /<[a-z][^>]*>/i.test(raw);
    const wrapped = isHtml
      ? raw
      : '<ul>' + splitLines(raw).map((l) => `<li>${l}</li>`).join('') + '</ul>';
    return DOMPurify.sanitize(wrapped, { USE_PROFILES: { html: true } });
  }, [data?.brand_responsibilities, tr?.brand_responsibilities]);

  const shell = (msg: string, tone: string) => (
    <div style={{ fontFamily: FONT, background: LT.paper, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: tone, fontSize: 15 }}>
      {msg}
    </div>
  );
  if (status === 'loading') return shell('Loading…', LT.ink500);
  if (status === 'notfound' || !data) return shell('This partnership page is not available.', LT.ink500);
  if (status === 'error') return shell('Could not load this page right now. Please try again later.', '#b54338');

  const c = data.campaign;
  const t = makeT(effLang);
  const intros = splitParagraphs(pick(data.intro, tr?.intro));
  const pageIntro = pick(data.page_intro, tr?.page_intro);
  const extraTitle = pick(data.extra_section_title, tr?.extra_section_title);
  const statsTitle = pick(data.stats_title, tr?.stats_title);
  const daysNote = pick(data.days_note, tr?.days_note);
  const stats = (data.stats ?? []).map((s, i) => {
    const x = tr?.stats?.[i];
    return { value: pick(s.value, x?.value), label: pick(s.label, x?.label), note: pick(s.note, x?.note) };
  });
  const faq = tr?.faq?.some((q) => q.question?.trim()) ? tr.faq : data.faq;
  const disclaimer = pick(data.participation_disclaimer, tr?.participation_disclaimer);
  // Pages from before 2026-10-19 that really sold packs keep the pack
  // layout; every other page sells placements one by one (booking comes
  // next) and drops packs, add-ons, previews and the opt-out copy.
  const packsMode = data.uses_packs;
  // Minimum shopper discount; null = the page asks for none (Black Friday).
  const minDisc = data.min_discount_pct != null ? Number(data.min_discount_pct) : null;
  const discountOk = minDisc == null || discount >= minDisc;

  // The overview tiles are derived from features + add-ons (name +
  // explanation come from them); plus any self-labelled extras.
  const hiddenIds = data.preview_hidden ?? {};
  const overviewItems: PartnershipPreview[] = [
    ...data.features
      .filter((f) => !hiddenIds[f.id])
      .map((f) => ({
        id: f.id, label: f.label, caption: f.hint, shape: 'landscape' as const,
        image: data.preview_images?.[f.id],
      })),
    ...data.addons
      .filter((a) => !hiddenIds[a.id])
      .map((a) => ({
        id: a.id, label: a.label, caption: a.description, shape: 'landscape' as const,
        image: data.preview_images?.[a.id],
      })),
    ...(data.extra_previews ?? []),
  ];
  const contacts: PartnershipContact[] = data.contacts && data.contacts.length ? data.contacts : [];

  const pack = data.packs.find((p) => p.id === selectedPack) ?? null;
  // Rate-card keys of what the visitor would get: the pack's features plus
  // eligible chosen add-ons; with no pack selected, everything we map.
  const mapItemIds: string[] = pack
    ? [...pack.includes, ...data.addons.filter((a) => selectedAddons.has(a.id) && (!a.available_for || a.available_for.length === 0 || a.available_for.includes(pack.id))).map((a) => a.id)]
    : [...data.features.map((f) => f.id), ...data.addons.map((a) => a.id)];
  const mapKeys = new Set(mapItemIds.map((id) => RATE_BY_PARTNERSHIP_ITEM[id]).filter(Boolean));
  const mapPages = pagesWithAny(mapKeys);
  const packNameById = new Map(data.packs.map((p) => [p.id, p.name]));
  const addonEligible = (a: PartnershipAddon) =>
    !!selectedPack
    && (!a.available_for || a.available_for.length === 0 || a.available_for.includes(selectedPack));
  const addonEligibilityLabel = (a: PartnershipAddon): string | null => {
    if (!selectedPack) return 'Select a pack first';
    if (!a.available_for || a.available_for.length === 0) return null;
    return `Available with ${a.available_for.map((id) => packNameById.get(id) ?? id).join(' & ')}`;
  };
  const addonsList = data.addons.filter((a) => selectedAddons.has(a.id) && addonEligible(a));
  // Booking pages: what's ticked, by placement, with its price.
  const booking = data.booking;
  const bookedLines = (booking?.placements ?? []).flatMap((pl) =>
    pl.slots.filter((sl) => selectedSlots.has(sl.id)).map((sl) => ({ pl, sl, price: priceOf(sl) })));
  const signedUp = !!booking?.signed_up;
  const proposedCount = booking?.proposed_slots?.length ?? 0;
  const proposedGone = booking?.proposed_gone ?? [];
  const total = packsMode
    ? (pack?.price ?? 0) + addonsList.reduce((s, a) => s + a.price, 0)
    : bookedLines.reduce((n, l) => n + (l.price ?? 0), 0);
  const onRequest = bookedLines.filter((l) => l.price == null).length;
  const hasSelection = packsMode ? !!pack || addonsList.length > 0 : bookedLines.length > 0;

  // Shared submit handler — used by both the in-card "Confirm my
  // participation" button and the sticky-footer CTA (opt-in mode).
  const runBookingSubmit = () => {
    if (!bookedLines.length || submitState === 'submitting') return;
    setSubmitState('submitting');
    setSubmitError(null);
    submitPublicOptIn({
      slug,
      token,
      slots: bookedLines.map((l) => l.sl.id),
      agreed,
      discount: minDisc != null ? `${discount}%` : '',
      brand_note: brandNote.trim(),
    })
      .then(() => {
        setSubmitState('done');
        fetchPublicPartnership(slug, token).then(setData).catch(() => {});
      })
      .catch((err) => {
        const d = (err as { response?: { data?: { detail?: string; taken?: number[]; discount?: string; agreed?: string; slots?: string } } })?.response?.data;
        if (d?.taken?.length) {
          // Someone was faster: drop those picks and show fresh availability.
          setSelectedSlots((prev) => new Set([...prev].filter((x) => !d.taken!.includes(x))));
          fetchPublicPartnership(slug, token).then(setData).catch(() => {});
        }
        setSubmitState('error');
        setSubmitError(String(d?.detail || d?.discount || d?.agreed || d?.slots || t('couldNotSignUp')));
      });
  };

  const runOptInSubmit = () => {
    if (!packsMode) { runBookingSubmit(); return; }
    if (!pack || submitState === 'submitting') return;
    setSubmitState('submitting');
    setSubmitError(null);
    submitPublicOptIn({
      slug,
      token,
      selected_pack: pack.id,
      selected_addons: Array.from(selectedAddons),
      discount: `${discount}%`,
      brand_note: brandNote.trim(),
    })
      .then(() => setSubmitState('done'))
      .catch((err) => {
        const detail = (err as { response?: { data?: { detail?: string; selected_pack?: string } } })?.response?.data;
        const msg = (detail?.detail || detail?.selected_pack) ?? 'Could not submit — please try again.';
        setSubmitState('error');
        setSubmitError(String(msg));
      });
  };

  const brandLabel = data.response_brand?.name || '';

  // Tier-ladder feature order: cheapest pack's features first.
  const packsByPrice = [...data.packs].sort((a, b) => a.price - b.price);
  const tierRank = (fid: string) => {
    for (let i = 0; i < packsByPrice.length; i++) if (packsByPrice[i].includes.includes(fid)) return i;
    return packsByPrice.length;
  };
  const sortedFeatures = data.features
    .map((f, idx) => ({ f, idx, rank: tierRank(f.id) }))
    .sort((a, b) => a.rank - b.rank || a.idx - b.idx)
    .map((x) => x.f);

  const toggleAddon = (id: string) =>
    setSelectedAddons((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const primaryEmail = contacts[0]?.email ?? '';
  const allEmails = contacts.map((x) => x.email).filter(Boolean).join(',');
  const summary = (() => {
    const lines = [`Campaign: ${c.name}`];
    if (pack) lines.push(`Pack: ${pack.name} (${fmtEUR(pack.price)})`);
    if (addonsList.length) {
      lines.push('Add-ons:');
      addonsList.forEach((a) => lines.push(`  - ${a.label} (${a.price > 0 ? fmtEUR(a.price) : a.price_note || 'Free'})`));
    }
    if (!packsMode && bookedLines.length) {
      lines.push('Slots:');
      bookedLines.forEach((l) => lines.push(`  - ${l.pl.name}, ${l.sl.start}${l.sl.end !== l.sl.start ? ` → ${l.sl.end}` : ''} (${l.price != null ? fmtEUR(l.price) : 'price on request'})`));
    }
    if (hasSelection) lines.push(`Total: ${fmtEUR(total)}`);
    if (minDisc != null && discountOk && discount > 0) lines.push(`End-consumer discount: ${discount}%`);
    return lines.join('\n');
  })();
  const mailBody = hasSelection
    ? (packsMode
      ? `Hi,\n\nWe'd like to opt in to ${c.name} with the following:\n\n${summary}\n\nLooking forward to hearing from you.`
      : `Hi,\n\nWe'd like to book the following for ${c.name}:\n\n${summary}\n\nLooking forward to hearing from you.`)
    : `Hi,\n\nWe'd like to know more about the ${c.name} partnership.`;
  const mailLink = `mailto:${primaryEmail}${allEmails ? `?cc=${encodeURIComponent(contacts.slice(1).map((x) => x.email).join(','))}` : ''}${primaryEmail ? (allEmails && contacts.length > 1 ? '&' : '?') : ''}subject=${encodeURIComponent(`${c.name} – partnership`)}&body=${encodeURIComponent(mailBody)}`;

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const onSelectPack = (id: string) => setSelectedPack((prev) => (prev === id ? null : id));

  return (
    <div className="lt-pp-page-pad" style={{ fontFamily: FONT, fontWeight: 300, background: LT.paper, color: LT.ink, WebkitFontSmoothing: 'antialiased' }}>
      {/* Responsive overrides — see RESPONSIVE_CSS at top of file */}
      <style>{RESPONSIVE_CSS}</style>

      {/* Closed-for-opt-in banner — sits above the header so it's
          impossible to miss. Doreen flips this from the Logistics tab
          once outreach is over. */}
      {data.closed_for_opt_in && (
        <div style={{
          background: LT.yellow,
          color: LT.ink,
          padding: '10px 24px',
          textAlign: 'center',
          fontSize: 12.5,
          fontWeight: 600,
          letterSpacing: '0.04em',
        }}>
          {packsMode
            ? 'Opt-ins for this campaign are closed. The page stays online for reference — contact your LampTwist contact for any late requests.'
            : t('closedBanner')}
        </div>
      )}

      {/* Single header — combines the LampTwist wordmark + the
          per-brand welcome when the visitor came in via a token.
          No-token visits get a quieter light strip with just the
          wordmark + the partnership eyebrow. */}
      {optInMode && brandLabel ? (
        <div style={{
          background: LT.ink,
          color: LT.paper,
          position: 'sticky', top: 0, zIndex: 30,
        }}>
          <div
            className="lt-pp-strip"
            style={{
              ...WRAP,
              display: 'flex', alignItems: 'center', gap: 24,
              justifyContent: 'space-between',
              padding: '18px 32px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, minWidth: 0 }}>
              <img
                src="/lamptwist-wordmark.svg"
                alt="LampTwist"
                style={{
                  height: 30, display: 'block',
                  // Wordmark is drawn dark; invert it for the dark bar.
                  filter: 'brightness(0) invert(1)',
                  flexShrink: 0,
                }}
              />
              <span
                className="lt-pp-strip-eyebrow"
                style={{
                  fontSize: 11, letterSpacing: '0.16em',
                  textTransform: 'uppercase', color: LT.ink300,
                  paddingLeft: 18, borderLeft: `1px solid rgba(251,251,250,0.18)`,
                }}
              >
                Partnership proposal
              </span>
            </div>
            <div style={{ textAlign: 'right', minWidth: 0 }}>
              <div
                className="lt-pp-welcome-title"
                style={{
                  fontSize: 22, lineHeight: 1.15,
                  fontWeight: 300, color: LT.paper,
                  letterSpacing: '-0.01em',
                }}
              >
                {t('welcome')} <span style={{ color: LT.yellow }}>{brandLabel}</span>
              </div>
              <div style={{
                fontSize: 12, color: LT.ink300, marginTop: 2,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {packsMode
                  ? <>Pick your packs &amp; extras, then confirm at the bottom.</>
                  : t('welcomeSub')}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ background: LT.paper, borderBottom: `1px solid ${LT.border}`, position: 'sticky', top: 0, zIndex: 30 }}>
          <div className="lt-pp-strip" style={{ ...WRAP, display: 'flex', alignItems: 'center', gap: 18, padding: '18px 32px' }}>
            <img src="/lamptwist-wordmark.svg" alt="LampTwist" style={{ height: 30, display: 'block' }} />
            <span className="lt-pp-strip-eyebrow" style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: LT.ink500, paddingLeft: 18, borderLeft: `1px solid ${LT.border}` }}>
              Partnership proposal
            </span>
          </div>
        </div>
      )}

      {/* Hero */}
      <section style={{ background: LT.paper }}>
        <div className="lt-pp-hero" style={{ ...WRAP, padding: '96px 32px 110px', position: 'relative' }}>
          {!packsMode && (
            <div role="group" aria-label="Language" style={{ position: 'absolute', top: 28, right: 32, display: 'inline-flex', gap: 2, padding: 3, borderRadius: LT.rPill, border: `1px solid ${LT.border}`, background: LT.paper }}>
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  aria-pressed={effLang === l.code}
                  onClick={() => setLang(l.code)}
                  style={{
                    border: 0, cursor: 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 600, letterSpacing: '0.06em',
                    padding: '6px 12px', borderRadius: LT.rPill,
                    background: effLang === l.code ? LT.ink : 'transparent', color: effLang === l.code ? LT.paper : LT.ink500,
                  }}
                >
                  {l.label}
                </button>
              ))}
            </div>
          )}
          <SplitHeading text={c.name} size="clamp(2.5rem, 5.2vw, 4rem)" />
          {/* Every intro paragraph: the first as the lead, the rest a size down
              (only the first used to show — Doreen, 2026-10-27). */}
          {intros.map((para, i) => (
            <p
              key={i}
              className={i === 0 ? 'lt-pp-hero-lead' : 'lt-pp-hero-more'}
              style={i === 0
                ? { fontSize: 22, lineHeight: 1.45, color: LT.ink700, marginTop: 32, maxWidth: '74ch' }
                : { fontSize: 18, lineHeight: 1.6, color: LT.ink700, margin: '16px 0 0', maxWidth: '74ch' }}
            >
              {para}
            </p>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 40, flexWrap: 'wrap' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, padding: '14px 26px', borderRadius: LT.rPill, background: LT.ink, color: LT.paper, fontSize: 15, letterSpacing: '0.04em' }}>
              <IconCalendar /> {packsMode ? rangeLabel(c.start_date, c.end_date) : fmtRangeL(effLang, c.start_date, c.end_date)}
            </div>
          </div>
          {data.show_brands && c.brands && c.brands.length > 0 && (
            <p className="lt-pp-hero-brands" style={{ fontSize: 14, lineHeight: 1.5, color: LT.ink500, margin: '20px 0 0' }}>
              {t('proposalFor')}{' '}
              <span style={{ color: LT.ink, fontWeight: 500 }}>
                {c.brands.map((b) => b.name).join(', ')}
              </span>
            </p>
          )}

          {/* Second tier — what this page is, before the packs */}
          <div
            className="lt-pp-hero-cta"
            style={{
              marginTop: 56,
              paddingTop: 36,
              borderTop: `1px solid ${LT.border}`,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 28,
              flexWrap: 'wrap',
            }}
          >
            {(pageIntro || packsMode) && (
              <p
                style={{
                  flex: '1 1 360px',
                  fontSize: 18,
                  lineHeight: 1.6,
                  color: LT.ink700,
                  margin: 0,
                  maxWidth: '64ch',
                  paddingLeft: 16,
                  borderLeft: `3px solid ${LT.yellow}`,
                }}
              >
                {pageIntro
                  || 'Below are the visibility packs for this campaign — each one bundles where and how your brand shows up. Pick the one that fits, add any extras, and reply to opt in.'}
              </p>
            )}
            {(packsMode || booking) && (
            <a
              href={packsMode ? '#packs' : '#book'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontSize: 13, letterSpacing: '0.04em', color: LT.paper, background: LT.ink, textDecoration: 'none', padding: '14px 24px', borderRadius: LT.rPill, whiteSpace: 'nowrap' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = LT.ink700)}
              onMouseLeave={(e) => (e.currentTarget.style.background = LT.ink)}
            >
              {packsMode ? 'See the packs' : t('bookYourSpots')} <IconArrow />
            </a>
            )}
          </div>
        </div>
        <div style={{ height: 1, background: LT.border }} />
      </section>

      {/* Numbers strip — every page with numbers (booking pages too since
          2026-10-27): a big heading and the figures in yellow, right under
          the intro so brands see the size of the campaign first. */}
      {stats.length > 0 && (
        <section className="lt-pp-stats-section" style={{ background: LT.ink, color: LT.paper }}>
          <div className="lt-pp-stats-wrap" style={{ ...WRAP, padding: '88px 32px 92px' }}>
            <Eyebrow color={LT.yellow}>{t('byTheNumbers')}</Eyebrow>
            {statsTitle && (
              <h2 className="lt-pp-stats-title" style={{ fontSize: 44, lineHeight: 1.05, letterSpacing: '-0.02em', fontWeight: 300, margin: '16px 0 0', color: LT.paper }}>
                {statsTitle}
              </h2>
            )}
            <div className="lt-pp-stats-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${stats.length}, 1fr)`, gap: 0, marginTop: 52 }}>
              {stats.map((s, i) => (
                <div key={i} className="lt-pp-stats-cell" style={{ borderLeft: i === 0 ? 'none' : '1px solid rgba(251,251,250,0.18)', paddingLeft: i === 0 ? 0 : 28, paddingRight: 28 }}>
                  <div className="lt-pp-stats-value" style={{ fontSize: 'clamp(40px, 3.6vw, 68px)', lineHeight: 1, letterSpacing: '-0.03em', fontWeight: 300, color: LT.yellow }}>{s.value}</div>
                  <div style={{ fontSize: 16, color: LT.paper, marginTop: 14, lineHeight: 1.4 }}>{s.label}</div>
                  {s.note && (
                    <div style={{ fontSize: 12, color: LT.ink300, marginTop: 6, lineHeight: 1.5 }}>{s.note}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Previews — moved above the packs so brands get a tease of the
          assets first ("what it looks like") before they shop. */}
      {packsMode && overviewItems.length > 0 && (
        <section style={{ background: LT.paper }}>
          <div className="lt-pp-section" style={{ ...WRAP, padding: '120px 32px' }}>
            <div className="lt-pp-section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 56, gap: 24, flexWrap: 'wrap' }}>
              <div>
                <Eyebrow>What it looks like</Eyebrow>
                <div style={{ marginTop: 18 }}>
                  <SplitHeading className="lt-pp-section-title" text="The assets live" size={64} max="14ch" />
                </div>
              </div>
              <p style={{ fontSize: 16, lineHeight: 1.55, color: LT.ink700, margin: 0, maxWidth: '42ch' }}>
                Here's where your brand can show up during the campaign. The actual mix depends on the pack — and any add-ons — you pick below.
              </p>
            </div>
            <div className="lt-pp-previews-cols" style={{ columnCount: 3, columnGap: 18 }}>
              {overviewItems.map((p) => (
                <PreviewCard key={p.id} preview={p} onOpen={setLightbox} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Booking: the campaign's pages with the spots to book, per placement its days. */}
      {!packsMode && booking && (
        <section id="book" style={{ background: LT.bone }}>
          <div className="lt-pp-section" style={{ ...WRAP, padding: '100px 32px' }}>
            <div className="lt-pp-section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 32, gap: 24, flexWrap: 'wrap' }}>
              <div>
                <Eyebrow>{signedUp ? t('yourBooking') : t('bookYourSpots')}</Eyebrow>
                <div style={{ marginTop: 18 }}>
                  <SplitHeading className="lt-pp-section-title" text={signedUp ? t('youreIn') : t('pickYourDays')} size={64} max="14ch" />
                </div>
              </div>
              <p style={{ fontSize: 16, lineHeight: 1.55, color: LT.ink700, margin: 0, maxWidth: '44ch' }}>
                {signedUp
                  ? t('bookedIntro')
                  : t('bookIntro')}
              </p>
            </div>
            {!signedUp && !data.closed_for_opt_in && (proposedCount > 0 || proposedGone.length > 0) && (
              <div style={{ margin: '-8px 0 28px', padding: '14px 18px', borderRadius: 12, background: LT.paper, border: `1px solid ${LT.yellow}`, fontSize: 15, lineHeight: 1.5, color: LT.ink700 }}>
                {proposedCount > 0 && <div>✨ {t('proposedIntro')}</div>}
                {proposedGone.length > 0 && (
                  <div style={{ marginTop: proposedCount > 0 ? 6 : 0, color: LT.ink500 }}>
                    {t('proposedGone', { list: proposedGone.map((g) => {
                      const pl = booking.placements.find((p) => p.slots.some((s) => s.id === g.id));
                      const name = (effLang !== 'en' && pl?.translations?.[effLang]?.name) || g.name;
                      return `${name} · ${fmtDayMonth(effLang, g.start)}`;
                    }).join(', ') })}
                  </div>
                )}
              </div>
            )}
            <BookingSection
              booking={booking}
              start={c.start_date}
              end={c.end_date}
              selected={selectedSlots}
              onToggle={toggleSlot}
              locked={signedUp || !!data.closed_for_opt_in}
              daysNote={daysNote}
              lang={effLang}
              t={t}
            />
          </div>
        </section>
      )}

      {/* Placement map — the real pages with the offer's spots framed. */}
      {packsMode && mapPages.length > 0 && (
        <section style={{ background: LT.bone }}>
          <div className="lt-pp-section" style={{ ...WRAP, padding: '120px 32px' }}>
            <div className="lt-pp-section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 40, gap: 24, flexWrap: 'wrap' }}>
              <div>
                <Eyebrow>Where you'll appear</Eyebrow>
                <div style={{ marginTop: 18 }}>
                  <SplitHeading className="lt-pp-section-title" text="On the real pages" size={64} max="14ch" />
                </div>
              </div>
              <p style={{ fontSize: 16, lineHeight: 1.55, color: LT.ink700, margin: 0, maxWidth: '42ch' }}>
                {pack
                  ? `The frames show the spots included in ${pack.name}${addonsList.length ? ' and your add-ons' : ''}. Hover a frame for details.`
                  : 'Every spot we sell on lamptwist.com, framed on the live pages. Pick a pack below to see yours.'}
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18, alignItems: 'start' }}>
              {mapPages.map((pg) => (
                <div key={pg.key}>
                  <div style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: LT.ink500, marginBottom: 8 }}>{pg.title}</div>
                  <PlacementMap page={pg} highlight={mapKeys} maxHeight={640} detail={(sp) => <span>{sp.label}</span>} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Packs */}
      {packsMode && (
      <section id="packs" style={{ background: LT.bone }}>
        <div className="lt-pp-section" style={{ ...WRAP, padding: '120px 32px' }}>
          <div className="lt-pp-section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 56, gap: 24, flexWrap: 'wrap' }}>
            <div>
              <Eyebrow>Step 01 · Choose a pack</Eyebrow>
              <div style={{ marginTop: 18 }}>
                <SplitHeading className="lt-pp-section-title" text="Visibility packs" size={64} max="14ch" />
              </div>
            </div>
            <p style={{ fontSize: 16, lineHeight: 1.55, color: LT.ink700, margin: 0, maxWidth: '46ch' }}>
              Each pack stacks on the previous — pick the floor that matches your ambition. With any pack you have its features for the whole campaign.
            </p>
          </div>
          <div className="lt-pp-packs-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${data.packs.length}, 1fr)`, gap: 18, alignItems: 'stretch' }}>
            {data.packs.map((p) => (
              <PackCard key={p.id} pack={p} features={sortedFeatures} selected={selectedPack === p.id} onSelect={() => onSelectPack(p.id)} />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Add-ons */}
      {packsMode && data.addons.length > 0 && (
        <section style={{ background: LT.paper }}>
          <div className="lt-pp-section" style={{ ...WRAP, padding: '120px 32px' }}>
            <div className="lt-pp-section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 56, gap: 24, flexWrap: 'wrap' }}>
              <div>
                <Eyebrow>Step 02 · Compound your reach</Eyebrow>
                <div style={{ marginTop: 18 }}>
                  <SplitHeading className="lt-pp-section-title" text="Optional add-ons" size={64} max="14ch" />
                </div>
              </div>
              <p style={{ fontSize: 16, lineHeight: 1.55, color: LT.ink700, margin: 0, maxWidth: '42ch' }}>
                Stack any of these on top of your pack. Limited-availability add-ons run a set number of days within the campaign — we pick the dates based on demand.
              </p>
            </div>
            <div className="lt-pp-addons-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
              {data.addons.map((a) => {
                const eligible = addonEligible(a);
                return (
                  <AddonCard
                    key={a.id}
                    addon={a}
                    selected={selectedAddons.has(a.id) && eligible}
                    eligible={eligible}
                    eligibilityLabel={addonEligibilityLabel(a)}
                    onToggle={() => { if (eligible) toggleAddon(a.id); }}
                  />
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Optional free-text section — only when toggled on and non-empty */}
      {data.show_extra_section && responsibilitiesHtml && (
        <section style={{ background: LT.bone }}>
          <div className="lt-pp-section lt-pp-two-col" style={{ ...WRAP, padding: '120px 32px', display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 72 }}>
            <div>
              <Eyebrow>{t('goodToKnow')}</Eyebrow>
              <h2 className="lt-pp-card-title" style={{ fontSize: 44, lineHeight: 1.05, letterSpacing: '-0.02em', fontWeight: 300, margin: '18px 0 0' }}>
                {extraTitle || 'What’s in it for you?'}
              </h2>
            </div>
            <div
              className="lt-pp-extras-rt"
              style={{ fontSize: 17, lineHeight: 1.55, color: LT.ink }}
              dangerouslySetInnerHTML={{ __html: responsibilitiesHtml }}
            />
          </div>
        </section>
      )}

      {/* FAQ — only renders when there's at least one entry */}
      {Array.isArray(faq) && faq.filter((q) => q.question?.trim() || q.answer?.trim()).length > 0 && (
        <section style={{ background: LT.paper }}>
          <div className="lt-pp-section" style={{ ...WRAP, padding: '100px 32px' }}>
            <Eyebrow>{t('goodToKnow')}</Eyebrow>
            <h2 className="lt-pp-section-title" style={{ fontSize: 44, lineHeight: 1.05, letterSpacing: '-0.02em', fontWeight: 300, margin: '14px 0 36px' }}>
              {t('faqTitle')}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 880 }}>
              {faq.map((qa, i) => (
                (qa.question?.trim() || qa.answer?.trim()) && (
                  <div key={i} style={{ borderTop: i === 0 ? `1px solid ${LT.border}` : 'none', borderBottom: `1px solid ${LT.border}`, padding: '18px 0' }}>
                    <div style={{ fontSize: 17, fontWeight: 600, color: LT.ink, marginBottom: 8, letterSpacing: '-0.005em' }}>
                      {qa.question}
                    </div>
                    {qa.answer && (
                      <div style={{ fontSize: 14.5, lineHeight: 1.6, color: LT.ink700, whiteSpace: 'pre-wrap' }}>
                        {qa.answer}
                      </div>
                    )}
                  </div>
                )
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Auto-include reassurance — sits just before the "how to join"
          cards so it reads as "if you're going to sit it out, just know
          you're still in the free pack". */}
      {packsMode && data.auto_include_note && data.auto_include_note.trim() && (
        <section style={{ background: LT.bone }}>
          <div className="lt-pp-section" style={{ ...WRAP, padding: '80px 32px 40px' }}>
            <div className="lt-pp-autoincl" style={{
              background: LT.paper, border: `1px solid ${LT.border}`,
              borderRadius: LT.rCard, padding: '24px 32px',
              display: 'flex', gap: 18, alignItems: 'flex-start',
            }}>
              <span style={{
                display: 'inline-block', flexShrink: 0,
                fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase',
                fontWeight: 700, color: LT.ink, background: LT.yellow50,
                border: `1px solid ${LT.yellow}`,
                borderRadius: LT.rPill, padding: '4px 10px', marginTop: 2,
              }}>
                You're in
              </span>
              <p style={{ fontSize: 14.5, lineHeight: 1.55, color: LT.ink700, margin: 0, maxWidth: '60ch' }}>
                {data.auto_include_note}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Deadline + Contacts */}
      <section style={{ background: LT.bone }}>
        <div className="lt-pp-section lt-pp-two-col-equal" style={{ ...WRAP, padding: '100px 32px', display: 'grid', gridTemplateColumns: optInMode ? '1fr 2.5fr' : '1fr 1fr', gap: 32 }}>
          <div className="lt-pp-card" style={{ background: LT.paper, border: `1px solid ${LT.border}`, borderRadius: LT.rCard, padding: '44px 40px', display: 'flex', flexDirection: 'column', gap: 18 }}>
            <Eyebrow>{data.confirmation_deadline ? (packsMode ? 'Confirm by' : t('bookBy')) : packsMode ? 'How to join' : t('howToJoin')}</Eyebrow>
            <div className="lt-pp-card-title" style={{ fontSize: 44, lineHeight: 1.05, letterSpacing: '-0.025em', fontWeight: 300 }}>
              {data.confirmation_deadline ? (packsMode ? fmtLong(data.confirmation_deadline) : fmtLongL(effLang, data.confirmation_deadline)) : packsMode ? 'Reply when ready' : t('firstCome')}
            </div>
            <p style={{ fontSize: 14, lineHeight: 1.55, color: LT.ink700, margin: 0, maxWidth: '40ch' }}>
              {packsMode
                ? "Reply with your pack and any add-ons. We'll confirm within two working days."
                : t('howToJoinText')}
            </p>
          </div>
          <div ref={setOverviewEl} className="lt-pp-card" style={{ background: LT.ink, color: LT.paper, borderRadius: LT.rCard, padding: '44px 40px', display: 'flex', flexDirection: 'column', gap: 18 }}>
            {/* Shared header — only the title text differs between modes.
                token mode → "Confirm participation for [Brand]"
                no-token  → "Reply with your selection." */}
            <Eyebrow color={LT.yellow}>
              {submitState === 'done' ? (packsMode ? 'Done' : t('done')) : optInMode ? (packsMode ? 'Final step' : t('finalStep')) : (packsMode ? 'Ready to join' : t('readyToJoin'))}
            </Eyebrow>
            <div className="lt-pp-card-title" style={{ fontSize: 34, lineHeight: 1.1, letterSpacing: '-0.02em', fontWeight: 300, maxWidth: '20ch' }}>
              {submitState === 'done'
                ? (packsMode ? 'Thanks — got it!' : t('youreBooked'))
                : optInMode && data.response_brand?.name
                  ? <>{packsMode ? 'Confirm participation for' : t('signUp')} <span style={{ color: LT.yellow }}>{data.response_brand.name}</span></>
                  : optInMode
                    ? (packsMode ? 'Confirm my participation' : t('signUp'))
                    : (packsMode ? 'Reply with your selection.' : t('replyWithSelection'))}
            </div>

            {submitState === 'done' ? (
              <p style={{ fontSize: 14, lineHeight: 1.55, color: LT.ink300, margin: 0, maxWidth: '40ch' }}>
                {packsMode ? "Your selection is in. We'll get back to you to lock it in." : t('signedUpThanks')}
              </p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: packsMode || minDisc != null ? '1fr 1fr' : '1fr', gap: 32, marginTop: 6 }} className="lt-pp-optin-grid">
                {/* COLUMN 1 — Selection */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ fontSize: 10.5, color: LT.yellow, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700 }}>
                    {packsMode ? 'Your selection' : t('yourSelection')}
                  </div>
                  {!packsMode && hasSelection ? (
                    <>
                      <div style={{ display: 'grid', gap: 6 }}>
                        {bookedLines.map((l) => (
                          <div key={l.sl.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, fontSize: 14.5, color: LT.paper, borderBottom: '1px solid rgba(251,251,250,0.12)', padding: '4px 0 8px' }}>
                            <span>{(effLang !== 'en' && l.pl.translations?.[effLang]?.name) || l.pl.name} · {fmtDayMonth(effLang, l.sl.start)}</span>
                            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 10 }}>
                              <span style={{ color: LT.ink300, whiteSpace: 'nowrap' }}>{l.price != null ? fmtEUR(l.price) : t('onRequest').toLowerCase()}</span>
                              {!signedUp && !l.sl.mine && (
                                <button
                                  type="button"
                                  onClick={() => toggleSlot(l.sl.id)}
                                  aria-label={t('removePick')}
                                  title={t('removePick')}
                                  style={{ border: 0, background: 'transparent', color: LT.ink300, cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '0 2px' }}
                                >
                                  ✕
                                </button>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 6 }}>
                        <span style={{ fontSize: 32, letterSpacing: '-0.02em', fontWeight: 300, color: LT.paper, lineHeight: 1 }}>
                          {total > 0 ? fmtEUR(total) : '—'}
                        </span>
                        <span style={{ fontSize: 11.5, color: LT.ink300 }}>
                          {t('total')}{onRequest ? t('plusOnRequest', { n: onRequest }) : ''}
                        </span>
                      </div>
                    </>
                  ) : hasSelection ? (
                    <>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {pack && (
                          <span style={{ padding: '5px 12px', border: `1px solid ${LT.ink500}`, borderRadius: LT.rPill, fontSize: 11.5, color: LT.paper }}>
                            {pack.name} · {fmtEUR(pack.price)}
                          </span>
                        )}
                        {addonsList.map((a) => (
                          <span key={a.id} style={{ padding: '5px 12px', border: `1px solid ${LT.ink500}`, borderRadius: LT.rPill, fontSize: 11.5, color: LT.paper }}>
                            + {a.label} · {a.price > 0 ? fmtEUR(a.price) : a.price_note || 'Free'}
                          </span>
                        ))}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 6 }}>
                        <span style={{ fontSize: 32, letterSpacing: '-0.02em', fontWeight: 300, color: LT.paper, lineHeight: 1 }}>
                          {fmtEUR(total)}
                        </span>
                        <span style={{ fontSize: 11.5, color: LT.ink300 }}>total</span>
                      </div>
                      <div style={{ fontSize: 11, color: LT.ink300, marginTop: 2 }}>
                        {data.settlement_note}
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: 12.5, color: LT.ink300, fontStyle: 'italic' }}>
                      {packsMode
                        ? <>Pick a pack above to enable the {optInMode ? 'confirm' : 'reply'} button.</>
                        : signedUp ? t('signedUpSeeAbove') : t('tickAbove')}
                    </div>
                  )}
                </div>

                {minDisc != null && (<>
                {/* COLUMN 2 — End-consumer discount.
                    Starts at 0 so brands actively engage. Confirm /
                    reply button is gated on the 15% minimum. */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ fontSize: 10.5, color: LT.yellow, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700 }}>
                    {t('consumerDiscount')}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span style={{ fontSize: 32, fontWeight: 300, color: discount > 0 ? LT.paper : LT.ink500, letterSpacing: '-0.02em', lineHeight: 1 }}>{discount}%</span>
                    <span style={{ fontSize: 11.5, color: LT.ink300 }}>{t('offInShop')}</span>
                  </div>
                  <input
                    type="range"
                    min={0} max={30} step={1}
                    value={discount}
                    onChange={(e) => setDiscount(parseInt(e.target.value, 10))}
                    style={{ accentColor: LT.yellow, maxWidth: 220, marginTop: 2 }}
                  />
                  <div style={{ fontSize: 11, color: discount > 0 && !discountOk ? LT.yellow : LT.ink300, lineHeight: 1.5, maxWidth: '34ch' }}>
                    {discount === 0
                      ? t('dragSlider', { n: minDisc ?? 0 })
                      : !discountOk
                        ? t('minimumShort', { n: minDisc ?? 0, m: (minDisc ?? 0) - discount })
                        : t('whatShoppersSee')}
                  </div>
                  <div style={{
                    fontSize: 10.5, fontStyle: 'italic',
                    color: LT.ink500, lineHeight: 1.5, maxWidth: '34ch',
                    marginTop: 2,
                  }}>
                    {t('retailNote')}
                  </div>
                </div>
                </>)}

                {/* Full-width row — optional brand note. Only in opt-in
                    (token) mode; no-token mode falls back to mailto where
                    the brand can write whatever they want in the email. */}
                {optInMode && (
                  <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                    <label style={{ fontSize: 10.5, color: LT.yellow, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700 }}>
                      {t('anythingToAdd')} <span style={{ color: LT.ink300, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>{t('optional')}</span>
                    </label>
                    <textarea
                      value={brandNote}
                      onChange={(e) => setBrandNote(e.target.value)}
                      rows={3}
                      maxLength={2000}
                      placeholder={t('notePlaceholder')}
                      className="lt-pp-brand-note"
                      style={{
                        width: '100%',
                        background: 'rgba(251,251,250,0.06)',
                        border: `1px solid rgba(251,251,250,0.22)`,
                        borderRadius: 8,
                        padding: '10px 12px',
                        color: LT.paper,
                        fontFamily: 'inherit',
                        fontSize: 13,
                        lineHeight: 1.45,
                        resize: 'vertical',
                        outline: 'none',
                      }}
                      onFocus={(e) => { e.currentTarget.style.border = `1px solid ${LT.yellow}`; }}
                      onBlur={(e) => { e.currentTarget.style.border = `1px solid rgba(251,251,250,0.22)`; }}
                    />
                  </div>
                )}

                {/* Full-width row — CTA + disclaimer / contacts.
                    Only the CTA differs between modes:
                    token   → submit button
                    no-token → mailto: anchor (carries the selection + discount in the body) */}
                <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 18, borderTop: `1px solid rgba(251,251,250,0.18)`, marginTop: 4 }}>
                  {!packsMode && optInMode && !data.closed_for_opt_in && (
                    <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, lineHeight: 1.55, color: LT.ink300, maxWidth: '64ch', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        style={{ accentColor: LT.yellow, marginTop: 3, flex: 'none' }}
                      />
                      <span>
                        <strong style={{ color: LT.paper, fontWeight: 600 }}>{t('iAgree')}</strong>{' '}
                        {disclaimer.trim() || t('signingUpIsFinal')}
                      </span>
                    </label>
                  )}
                  {(() => {
                    // Pack pages: a pack + the minimum discount. Other pages:
                    // something selected (the booking picker comes next),
                    // the minimum discount and "I agree".
                    const canSubmit = packsMode
                      ? !!pack && discountOk
                      : hasSelection && discountOk && agreed;
                    // Partnership is locked once Doreen flips the
                    // "Close opt-ins" switch — the form stays visible
                    // for reference but the submit button is replaced
                    // with a closed-state message.
                    if (data.closed_for_opt_in) {
                      return (
                        <div
                          style={{
                            padding: '14px 18px', borderRadius: LT.rCard,
                            background: 'rgba(251,251,250,0.06)',
                            border: '1px solid rgba(251,251,250,0.22)',
                            color: LT.paper,
                            maxWidth: '52ch',
                          }}
                        >
                          <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: LT.yellow, fontWeight: 700, marginBottom: 6 }}>
                            {packsMode ? 'Opt-ins closed' : t('bookingsClosed')}
                          </div>
                          <div style={{ fontSize: 13.5, lineHeight: 1.5, color: LT.ink300 }}>
                            {packsMode
                              ? "We've closed the form for this campaign. If you'd still like to join, reach out to your LampTwist contact and we'll see what we can do."
                              : t('bookingsClosedText')}
                          </div>
                        </div>
                      );
                    }
                    if (optInMode) {
                      return (
                        <button
                          type="button"
                          disabled={submitState === 'submitting' || !canSubmit}
                          onClick={runOptInSubmit}
                          title={packsMode && !pack ? 'Pick a pack first.' : !discountOk ? t('setDiscountMin', { n: minDisc ?? 0 }) : !packsMode && !agreed ? t('tickAgreeFirst') : undefined}
                          style={{
                            fontSize: 15, color: '#fff', textDecoration: 'none',
                            display: 'inline-flex', alignItems: 'center', gap: 10,
                            padding: '13px 24px', borderRadius: LT.rPill,
                            background: LT.cta, width: 'fit-content',
                            border: 'none', cursor: canSubmit ? 'pointer' : 'not-allowed',
                            opacity: canSubmit ? 1 : 0.55,
                          }}
                          onMouseEnter={(e) => { if (canSubmit) (e.currentTarget.style.background = LT.ctaHover); }}
                          onMouseLeave={(e) => (e.currentTarget.style.background = LT.cta)}
                        >
                          {submitState === 'submitting' ? (packsMode ? 'Sending…' : t('sending')) : packsMode ? 'Confirm my participation' : t('signUp')}
                        </button>
                      );
                    }
                    if (!packsMode) {
                      // Booking page opened without a brand's personal link:
                      // booking is final, so it only happens through that link.
                      return (
                        <div style={{ display: 'grid', gap: 10, maxWidth: '60ch' }}>
                          {disclaimer.trim() && (
                            <div style={{ fontSize: 12.5, lineHeight: 1.55, color: LT.ink300 }}>{disclaimer}</div>
                          )}
                          <div style={{ padding: '12px 16px', borderRadius: LT.rCard, background: 'rgba(251,251,250,0.06)', border: '1px solid rgba(251,251,250,0.22)', fontSize: 13.5, lineHeight: 1.5, color: LT.paper }}>
                            <span dangerouslySetInnerHTML={{ __html: t('personalLink') }} />
                          </div>
                          <a href={mailLink} style={{ fontSize: 13, color: LT.yellow, textDecoration: 'underline', width: 'fit-content' }}>
                            {t('noInvitation')}
                          </a>
                        </div>
                      );
                    }
                    return (
                      <a
                        href={mailLink}
                        style={{
                          fontSize: 15, textDecoration: 'none',
                          display: 'inline-flex', alignItems: 'center', gap: 10,
                          padding: '13px 24px', borderRadius: LT.rPill,
                          background: canSubmit ? LT.cta : LT.yellow,
                          color: canSubmit ? '#fff' : LT.ink,
                          width: 'fit-content',
                        }}
                        onMouseEnter={(e) => { if (canSubmit) e.currentTarget.style.background = LT.ctaHover; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = canSubmit ? LT.cta : LT.yellow; }}
                      >
                        <IconMail /> {canSubmit ? 'Reply to opt in' : 'Talk to us first'}
                      </a>
                    );
                  })()}
                  {submitError && (
                    <div style={{ fontSize: 12.5, color: '#ffb4b4', maxWidth: '40ch' }}>
                      {submitError}
                    </div>
                  )}
                  {packsMode && optInMode && data.participation_disclaimer && data.participation_disclaimer.trim() && (
                    <div style={{
                      fontSize: 12, lineHeight: 1.55, color: LT.ink300,
                      maxWidth: '60ch',
                    }}>
                      {data.participation_disclaimer}
                    </div>
                  )}
                  {!optInMode && contacts.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 2 }}>
                      {contacts.map((ct) => (
                        <div key={ct.email} style={{ fontSize: 12.5, color: LT.ink300 }}>
                          {ct.name} · {ct.email}
                        </div>
                      ))}
                    </div>
                  )}
                  {packsMode && !optInMode && data.opt_out_note && data.opt_out_note.trim() && (
                    <div style={{
                      fontSize: 12.5, lineHeight: 1.55, color: LT.ink300,
                      maxWidth: '60ch', fontStyle: 'italic',
                    }}>
                      {data.opt_out_note}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: LT.bone, borderTop: `1px solid ${LT.border}` }}>
        <div style={{ ...WRAP, padding: '56px 32px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 24 }}>
          <img src="/lamptwist-wordmark.svg" alt="LampTwist" style={{ height: 24, display: 'block' }} />
          <div style={{ fontSize: 12, color: LT.ink500, display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <span>{packsMode ? 'Confidential · for the named partner' : t('confidential')}</span>
            <a href="https://www.lamptwist.com" target="_blank" rel="noreferrer" style={{ color: LT.ink, textDecoration: 'none' }}>lamptwist.com</a>
          </div>
        </div>
      </footer>

      {/* Sticky calculator retired — the bottom card now carries the
          full review (chips + total + discount + CTA) in both modes. */}

      {/* Booking pages: slim "cart" bar while the overview is off screen. */}
      {!packsMode && bookedLines.length > 0 && !overviewInView && !signedUp && submitState !== 'done' && (
        <button
          type="button"
          className="lt-pp-cartbar"
          onClick={() => overviewEl?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
          style={{
            position: 'fixed', left: '50%', bottom: 18, transform: 'translateX(-50%)', zIndex: 40,
            display: 'inline-flex', alignItems: 'center', gap: 14, padding: '12px 20px', borderRadius: LT.rPill,
            background: LT.ink, color: LT.paper, border: 0, cursor: 'pointer', font: 'inherit', fontSize: 14.5,
            boxShadow: '0 8px 24px rgba(43,65,56,0.28)', whiteSpace: 'nowrap', maxWidth: 'calc(100vw - 32px)',
          }}
        >
          <span>{t(bookedLines.length === 1 ? 'barSlot1' : 'barSlots', { n: bookedLines.length })}{total > 0 ? ` · ${fmtEUR(total)}` : ''}</span>
          <span style={{ color: LT.yellow, fontWeight: 600 }}>{t('reviewSignUp')} ↓</span>
        </button>
      )}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(20,19,15,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 32, cursor: 'zoom-out',
          }}
        >
          <img
            src={lightbox}
            alt=""
            style={{ maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain', display: 'block', boxShadow: '0 24px 80px rgba(0,0,0,0.5)' }}
          />
          <button
            type="button"
            onClick={() => setLightbox(null)}
            aria-label="Close"
            style={{
              position: 'fixed', top: 22, right: 26, width: 40, height: 40,
              borderRadius: '50%', border: `1px solid rgba(251,251,250,0.4)`,
              background: 'transparent', color: LT.paper, fontSize: 20,
              cursor: 'pointer', lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default CampaignPartnership;
